import { Analytics } from '@cobliteam/analytics';
import { useTranslation } from '@cobliteam/i18n';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { motion, type Variants } from 'motion/react';
import { useAIAssistant } from '../store';
import AIIcon from './AIIcon';

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const fadeInUp: Variants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
};

const suggestions = [
  { icon: 'shield-check' as const, labelKey: 'welcome.suggestion_security' as const, prompt: 'Analise a segurança da minha frota.' },
  { icon: 'chart-bar-increasing' as const, labelKey: 'welcome.suggestion_productivity' as const, prompt: 'Analise a produtividade da minha frota.' },
  { icon: 'car-taxi-front' as const, labelKey: 'welcome.suggestion_overview' as const, prompt: 'Analise minha frota de forma geral.' },
] as const;

const AIAssistantWelcome = () => {
  const { t } = useTranslation('ai');
  const { setPendingInput } = useAIAssistant();

  return (
    <motion.div className='flex flex-1 flex-col items-center justify-center px-4 py-6' variants={staggerContainer} initial='initial' animate='animate'>
      <div className='flex w-fit shrink-0 flex-col items-center text-center'>
        {/* Icon */}
        <motion.div variants={fadeInUp} className='mb-4 flex items-center justify-center'>
          <AIIcon shape='circle' variant='solid' iconSize='xl' />
        </motion.div>

        {/* Title */}
        <motion.div variants={fadeInUp}>
          <Typography variant='heading-lg' className='font-semibold text-gray-12'>
            {t('welcome.title')}
          </Typography>
        </motion.div>

        {/* Subtitle */}
        <motion.div variants={fadeInUp} className='mt-1.5'>
          <Typography variant='text-md' className='text-gray-11'>
            {t('welcome.subtitle')}
          </Typography>
        </motion.div>

        {/* Suggestion chips */}
        <motion.div variants={fadeInUp} className='mt-5 flex w-full flex-wrap justify-center gap-2'>
          {suggestions.map((s) => (
            <SuggestionChip
              key={s.labelKey}
              icon={s.icon}
              label={t(s.labelKey)}
              onClick={() => {
                Analytics.track('AI - Chat - Suggestion Clicked', { prompt: s.prompt, label: s.labelKey, source: 'sidepanel' });
                setPendingInput(s.prompt);
              }}
            />
          ))}
        </motion.div>
      </div>
    </motion.div>
  );
};

type SuggestionChipProps = {
  icon: 'shield-check' | 'chart-bar-increasing' | 'car-taxi-front';
  label: string;
  onClick: () => void;
  className?: string;
};

const SuggestionChip = ({ icon, label, onClick, className }: SuggestionChipProps) => (
  <button
    type='button'
    onClick={onClick}
    className={cn(
      'flex w-fit shrink-0 cursor-pointer items-center gap-2 rounded-full border border-gray-4 bg-white px-3.5 py-2 text-left transition-colors hover:border-gray-6 hover:bg-gray-1',
      className
    )}
  >
    <Icon name={icon} className='size-4 shrink-0 text-gray-10' />
    <Typography variant='text-sm' className='text-gray-11'>
      {label}
    </Typography>
  </button>
);

AIAssistantWelcome.displayName = 'AIAssistantWelcome';

export default AIAssistantWelcome;
